import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { GuidedInput } from "@/components/ui/guided-input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";
import { Plus, Shield, Lock, ChevronRight, Trash2, Loader2, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function RolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [grants, setGrants] = useState(new Set());
  const [newRole, setNewRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([
      apiFetch(`/roles?include_archived=${showArchived}`), apiFetch("/permissions")]);
    setRoles(r);
    setPermissions(p);
    setSelectedId((cur) => cur || r.find((x) => x.name === "Super Admin")?.id || r[0]?.id);
    setLoading(false);
  }, [showArchived]);

  useEffect(() => { load().catch((e) => { toast.error(e.message); setLoading(false); }); }, [load]);

  const current = roles.find((r) => r.id === selectedId);
  useEffect(() => { setGrants(new Set(current?.permissions || [])); }, [selectedId, current]);

  const toggle = (perm) => setGrants((prev) => {
    const s = new Set(prev);
    s.has(perm) ? s.delete(perm) : s.add(perm);
    return s;
  });

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/roles/${current.id}`, {
        method: "PUT",
        body: JSON.stringify({ name: current.name, description: current.description, permissions: [...grants] }),
      });
      toast.success("Permissions saved", { description: `Updated access for “${current.name}”.` });
      await load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newRole.trim()) return;
    try {
      const r = await apiFetch("/roles", { method: "POST", body: JSON.stringify({ name: newRole, permissions: ["View"] }) });
      toast.success(`Role “${newRole}” created`);
      setNewRole("");
      await load();
      setSelectedId(r.id);
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    try {
      await apiFetch(`/roles/${r.id}`, { method: "DELETE" });
      toast.success(`Role “${r.name}” deleted`);
      if (selectedId === r.id) setSelectedId(null);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const setArchived = async (r, archived) => {
    try {
      await apiFetch(`/roles/${r.id}/archive`, { method: "PUT", body: JSON.stringify({ archived }) });
      toast.success(archived ? `Role “${r.name}” archived` : `Role “${r.name}” restored`);
      if (archived && selectedId === r.id) setSelectedId(null);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="animate-fade-up">
      <PageHeader
        eyebrow="Settings" title="Roles & Permissions"
        subtitle="Roles are not hard-coded. Build custom roles and set exactly what each can do."
        actions={
          <Dialog>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-role-button"><Plus className="h-4 w-4" /> New Role</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Create a custom role</DialogTitle></DialogHeader>
              <div className="py-2">
                <GuidedInput label="Role name" required dataTestId="new-role-name" value={newRole}
                  onChange={(e) => setNewRole(e.target.value)} placeholder="e.g. Content Lead"
                  helperText="Give the role a clear, simple name your team will recognise."
                  exampleText="Content Lead, Social Media Admin, Analytics Admin"
                  whyItMatters="Custom roles let you match access to how Nirvana actually works, without waiting for code changes." />
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <DialogClose asChild><Button onClick={createRole} data-testid="create-role-confirm">Create Role</Button></DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <Card className="h-fit overflow-hidden shadow-soft" data-testid="roles-list">
          <div className="flex items-center justify-between border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Roles ({roles.length})</span>
            <button onClick={() => setShowArchived((s) => !s)} className="normal-case text-[11px] font-medium text-accent hover:underline" data-testid="toggle-archived-roles">
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </div>
          <div className="max-h-[520px] divide-y divide-border overflow-y-auto scrollbar-thin">
            {roles.map((r) => (
              <button key={r.id} onClick={() => setSelectedId(r.id)} data-testid={`role-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors", selectedId === r.id ? "bg-secondary" : "hover:bg-muted/50")}>
                <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", selectedId === r.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                  {r.is_system ? <Shield className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                    {r.name}
                    {!r.is_system && !r.archived && <span className="rounded bg-accent-subtle px-1.5 text-[9px] font-semibold uppercase text-accent">Custom</span>}
                    {r.archived && <span className="rounded bg-muted px-1.5 text-[9px] font-semibold uppercase text-muted-foreground" data-testid={`archived-badge-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>Archived</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{r.members} member{r.members === 1 ? "" : "s"}</div>
                </div>
                <ChevronRight className={cn("h-4 w-4", selectedId === r.id ? "text-primary" : "text-muted-foreground")} />
              </button>
            ))}
          </div>
        </Card>

        {current && (
          <Card className="p-6 shadow-soft" data-testid="permission-matrix">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-2xl font-semibold text-foreground">{current.name}</h3>
                {current.is_system && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">System role</span>}
              </div>
              {!current.is_system && (
                <div className="flex items-center gap-1">
                  {current.archived ? (
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setArchived(current, false)} data-testid="restore-role">
                      <RotateCcw className="h-4 w-4" /> Restore
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setArchived(current, true)} data-testid="archive-role">
                      <Archive className="h-4 w-4" /> Archive
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => remove(current)} data-testid="delete-role">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </div>
              )}
            </div>
            <p className="mb-5 text-sm text-muted-foreground">{current.description}</p>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Permissions</span>
              <span className="text-xs text-muted-foreground">{grants.size} of {permissions.length} enabled</span>
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
              {permissions.map((perm) => {
                const on = grants.has(perm);
                return (
                  <label key={perm} className={cn("flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-muted/50", on && "bg-secondary/60")}
                    data-testid={`perm-${perm.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Checkbox checked={on} onCheckedChange={() => toggle(perm)} />
                    <span className="text-sm text-foreground">{perm}</span>
                  </label>
                );
              })}
            </div>
            <div className="lotus-divider my-6" />
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-permissions">
                {saving ? "Saving…" : "Save Permissions"}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
